import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PapiDeviation } from '@wix/da-papi-types';
import classnames from 'classnames';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import ThumbMiddleContent from '@wix/da-ds/pkg/thumbs/legos/ThumbMiddleContent';
import ThumbStatusIndicator from '@wix/da-ds/pkg/thumbs/ThumbStatusIndicator';
import TierIcon16 from '@wix/da-ds/pkg/Icons/16x16/Tier';
import { Props as IconProps } from '@wix/da-ds/pkg/Icons/IconWrap';
import HoverOnly from '@wix/da-ds/pkg/thumbs/OverlayHelpers/HoverOnly';
import TierConsumerReduxModule from '../../../../../../Tier/redux/consumer/ModuleLoadable';
import { usePrimaryTier } from '../../../../../../Tier/PrimaryTierContext';
import { formatPrice } from '../../../../../TierProductCardDumb/utils';
import { useSubscribeButton } from '../../../../../../Button/SubscribeButton/useSubscribeButton';
import { getTierIsOnSale } from '../../../../../../utils/deviations';

import s from './NeedsSubscriptionMiddleContent.scss';

interface Props {
  deviation: PapiDeviation;
  thumbWidth?: number;
  thumbHeight?: number;
  className?: string;
}

const NeedsSubscriptionMiddleContent: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  thumbHeight,
  thumbWidth,
  className,
}) => {
  const tier = usePrimaryTier(deviation);
  const { onClick, href, biData, text } = useSubscribeButton(tier, {
    withSaleIndicator: true,
  });
  const { t } = useTranslation();

  // Check if tier has a discount/is on sale
  const isOnSale = getTierIsOnSale(tier);

  if (!tier) {
    return null;
  }

  const canShowDescription =
    thumbWidth && thumbWidth > 250
      ? thumbHeight && thumbHeight > 200
      : thumbHeight && thumbHeight > 230;
  const canShowCTA =
    thumbWidth && thumbWidth > 250
      ? thumbHeight && thumbHeight > 150
      : thumbHeight && thumbHeight > 200;
  const isUnreasonablySmall = thumbHeight && thumbHeight < 150;

  if (isUnreasonablySmall) {
    return (
      <HoverOnly reverseHidden>
        <ThumbMiddleContent
          icon={<ThumbStatusIndicator type="in-tier" size="large" />}
          className={className}
        />
      </HoverOnly>
    );
  }

  return (
    <BiLoggerContextProvider
      value={{
        referral_info: ReferralType.LOCKED_CONTENT,
      }}
    >
      <ThumbMiddleContent
        icon={<ThumbStatusIndicator type="in-tier" size="small" />}
        subtitle={
          canShowDescription
            ? t('deviationview.overlay.subscriber.perMonth', {
                postProcess: ['reactPostprocessor', 'featureBrancher'],
                dollarPrice: formatPrice(tier.tier?.dollarPrice),
                price: <span>{formatPrice(tier.tier?.dollarPrice)}</span>,
              })
            : undefined
        }
        buttonHref={href}
        buttonTitle={canShowCTA ? text : undefined}
        buttonStyle="solid"
        buttonClassname={isOnSale ? classnames(s['button'], s['on-sale']) : undefined}
        buttonIcon={RebalancedTierIcon16}
        onButtonClick={onClick}
        biData={biData}
        className={classnames(s['root'], className)}
      />
      <TierConsumerReduxModule />
    </BiLoggerContextProvider>
  );
};
NeedsSubscriptionMiddleContent.displayName = 'NeedsSubscriptionMiddleContent';

export default NeedsSubscriptionMiddleContent;

/** this is the crown icon and it's visual center of balance is very different from the
 *  mathmatical one. QA wants this to visually align better with text of the button,
 *  but `align-items: center` looks misaligned - so we use this to pull it up a bit */
const RebalancedTierIcon16: React.FC<IconProps> = ({ className, ...props }) => {
  return <TierIcon16 className={classnames(className, s['tier-icon-16-rebalance'])} {...props} />;
};
